<template>
  <div>
    <CompaniesStorePostsLayout
      :menu-list="menuList"
      :selected-tab-key="selectedPostStatusType"
      @click-menu="clickMenu"
    >
      <KkFilterModal
        :is-visible="isShowFilterModal"
        :filter-items="filterItems"
        :form-values="formValues"
        :total-count="filteredCount"
        @submit="handleResetPageAndSubmit"
        @reset="resetFilter"
        @change-check-box="handleChangeFilterModalCheckBox"
        @close="isShowFilterModal = false"
      />
      <template #nav-link>
        <div v-if="canCreatePostAuthority" class="c-button">
          <router-link
            :to="{ name: 'CompaniesStorePostsNew', params: { company_uuid: $route.params.company_uuid }}"
            class="c-button__item c-button__item--primary"
          >
            {{ $t('pages.CompaniesStorePost.createPost') }}
          </router-link>
        </div>
      </template>

      <template v-if="selectedPostStatusType === 'POST'">
        <Loading v-if="loading" />
        <template v-else>
          <div class="p-filtersA">
            <div class="p-filtersA__left">
              <div class="c-buttonFilter" @click="isShowFilterModal = true">
                {{ $t('pages.CompaniesStorePost.filters') }}
                <div class="c-buttonFilter__icon">
                  <Icon name="iconFilter" />
                </div>
              </div>
            </div>
          </div>
          <Card
            v-for="post in posts"
            :id="post.id"
            :key="post.id"
            :category="post.category"
            :status="post.status"
            :store-count="post.storeCount"
            :store-error-count="post.storeErrorCount"
            :topic-type="post.topicType"
            :summary="post.summary"
            :coupon-code="post.offer ? post.offer.couponCode : null"
            :post-title="post.title"
            :event="post.event"
            :call-to-action="post.callToAction"
            :post-images="post.postImages"
            :scheduled-at="post.scheduledAt ? new Date(post.scheduledAt) : null"
            :can-delete-post-authority="canDeletePostAuthority"
            :post-approval-flow="companyApprovalFlow.postApprovalFlow"
            :show-only-owner-authority="showOnlyOwnerAuthority"
            :author-name="post.authorName"
            :approver-name="post.approverName"
            :applicant-name="post.latestApplicantName"
            :repeat-configuration="post.repeatConfiguration ? toCamelCase(post.repeatConfiguration.kind) : null"
            :editable="post.editable"
            :selected-post-status-type="selectedPostStatusType"
            :local-post-views-search="post.localPostViewsSearch"
            :local-post-actions-call-to-action="post.localPostActionsCallToAction"
            @submit-category="handleUpdatePostCategory"
            @delete-post="clickDeletePost($event)"
            @open-preview-action-modal="openPreviewActionModal($event)"
            @open-post-stores-modal="openPostStoresModal($event)"
          />
          <p v-show="!posts.length && !loading">
            {{ emptyDescription }}
          </p>

          <div v-if="!loading && posts.length > 0" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">{{ $t('labels.perPageCount') }}</p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber($event)">
                  <option v-for="item in 6" :key="item" :value="displayNumbers[item - 1]">{{ displayNumbers[item - 1] }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="currentPage >= postsMeta.total_pages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
        </template>
      </template>
      <template v-if="selectedPostStatusType === 'RESERVING'">
        <Loading v-if="loading" />
        <template v-else>
          <div class="p-filtersA">
            <div class="p-filtersA__left">
              <div class="c-buttonFilter" @click="isShowFilterModal = true">
                {{ $t('pages.CompaniesStorePost.filters') }}
                <div class="c-buttonFilter__icon">
                  <Icon name="iconFilter" />
                </div>
              </div>
            </div>
          </div>
          <Card
            v-for="post in posts"
            :id="post.id"
            :key="post.id"
            :status="post.status"
            :store-count="post.storeCount"
            :store-error-count="post.storeErrorCount"
            :topic-type="post.topicType"
            :summary="post.summary"
            :coupon-code="post.offer ? post.offer.couponCode : null"
            :post-title="post.title"
            :event="post.event"
            :call-to-action="post.callToAction"
            :post-images="post.postImages"
            :scheduled-at="post.scheduledAt ? new Date(post.scheduledAt) : null"
            :can-delete-post-authority="canDeletePostAuthority"
            :post-approval-flow="companyApprovalFlow.postApprovalFlow"
            :show-only-owner-authority="showOnlyOwnerAuthority"
            :author-name="post.authorName"
            :approver-name="post.approverName"
            :applicant-name="post.latestApplicantName"
            :category="post.category"
            :repeat-configuration="post.repeatConfiguration ? toCamelCase(post.repeatConfiguration.kind) : null"
            :editable="post.editable"
            :selected-post-status-type="selectedPostStatusType"
            @delete-post="clickDeletePost($event)"
            @open-preview-action-modal="openPreviewActionModal($event)"
            @open-post-stores-modal="openPostStoresModal($event)"
          />

          <div v-if="!loading && posts.length > 0" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">{{ $t('labels.perPageCount') }}</p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber($event)">
                  <option v-for="item in 6" :key="item" :value="displayNumbers[item - 1]">{{ displayNumbers[item - 1] }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="currentPage >= postsMeta.total_pages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
        </template>
      </template>
      <template v-if="selectedPostStatusType === 'IN_PROGRESS'">
        <Loading v-if="loading" />
        <template v-else>
          <div class="requestingPostHeader">
            <div class="p-filtersA">
              <div class="p-filtersA__left">
                <div class="c-buttonFilter" @click="isShowFilterModal = true">
                  {{ $t('pages.CompaniesStorePost.filters') }}
                  <div class="c-buttonFilter__icon">
                    <Icon name="iconFilter" />
                  </div>
                </div>
              </div>
            </div>
            <div class="approvalSummary">
              <div class="item" :class="{ 'isActive': selectedApprovalStatusType === 'all' }">
                <span class="title">{{ $t('pages.CompaniesStorePost.index.requestingStatus.all') }}:</span>
                <div class="summary">
                  <span @click="clickApprovalSummary('all')">{{ approvalInitMeta.total_count }}</span>{{ $t('pages.CompaniesStorePost.index.requestingStatus.unit') }}
                </div>
              </div>
              <div class="item" :class="{ 'isActive': selectedApprovalStatusType === 'requesting' }">
                <span class="title">{{ $t('pages.CompaniesStorePost.index.requestingStatus.requesting') }}:</span>
                <div class="summary">
                  <span @click="clickApprovalSummary('requesting')">{{ approvalInitMeta.requesting_count }}</span>{{
                    $t('pages.CompaniesStorePost.index.requestingStatus.unit')
                  }}
                </div>
              </div>
              <div class="item" :class="{ 'isActive': selectedApprovalStatusType === 'rejected' }">
                <span class="title">{{ $t('pages.CompaniesStorePost.index.requestingStatus.rejected') }}:</span>
                <div class="summary">
                  <span @click="clickApprovalSummary('rejected')">{{ approvalInitMeta.rejected_count }}</span>{{ $t('pages.CompaniesStorePost.index.requestingStatus.unit') }}
                </div>
              </div>
            </div>
          </div>
          <div class="p-table__content">
            <table class="approvalTable">
              <thead>
                <tr>
                  <th style="width: 30%">
                    <span>状況</span>
                  </th>
                  <th style="width: 40%">
                    <span>投稿内容</span>
                  </th>
                  <th style="width: 20%">
                    <span>公開設定</span>
                  </th>
                  <th style="width: 10%" />
                </tr>
              </thead>
              <tbody>
                <template v-for="post in posts" :key="post.id">
                  <CompaniesStorePostsApprovalTableRow
                    :id="post.id"
                    :store-count="post.storeCount"
                    :approval-status="post.status"
                    :editor="post.lastEditedUserName"
                    :author-name="post.authorName"
                    :applicant-name="post.latestApplicantName"
                    :last-edited-at="post.lastEditedAt"
                    :topic-type="post.topicType"
                    :summary="post.summary"
                    :coupon-code="post.offer ? post.offer.couponCode : null"
                    :post-title="post.title"
                    :event="post.event"
                    :call-to-action="post.callToAction"
                    :post-images="post.postImages"
                    :scheduled-at="post.scheduledAt"
                    :category-name="post.category ? post.category.name : null"
                    :repeat-configuration="post.repeatConfiguration ? toCamelCase(post.repeatConfiguration.kind) : null"
                    :editable="post.editable"
                    @delete-approval-post="clickDeleteApprovalPost($event)"
                    @delete-request-approval-post="clickDeleteRequestApprovalPost($event)"
                    @open-preview-action-modal="openPreviewActionModal($event)"
                    @open-post-stores-modal="openPostStoresModal($event)"
                  />
                </template>
              </tbody>
            </table>
          </div>
          <div v-if="posts.length > 0" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">{{ $t('labels.perPageCount') }}</p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber($event)">
                  <option v-for="item in 6" :key="item" :value="displayNumbers[item - 1]">{{ displayNumbers[item - 1] }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="currentPage >= postsMeta.total_pages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
        </template>
      </template>
      <template v-if="selectedPostStatusType === 'DRAFT'">
        <Loading v-if="loading" />
        <div v-else class="p-table__content">
          <table class="approvalTable">
            <thead>
              <tr>
                <th style="width: 30%">
                  <span>状況</span>
                </th>
                <th style="width: 40%">
                  <span>投稿内容</span>
                </th>
                <th style="width: 20%">
                  <span>公開設定</span>
                </th>
                <th style="width: 10%" />
              </tr>
            </thead>
            <tbody>
              <template v-for="post in posts">
                <CompaniesStorePostsApprovalTableRow
                  v-if="post"
                  :id="post.id"
                  :key="post.id"
                  :store-count="post.storeCount"
                  approval-status="draft"
                  :editor="post.lastEditedUserName"
                  :author-name="post.authorName"
                  :applicant-name="post.latestApplicantName"
                  :last-edited-at="post.lastEditedAt"
                  :topic-type="post.topicType"
                  :summary="post.summary"
                  :coupon-code="post.offer ? post.offer.couponCode : null"
                  :post-title="post.title"
                  :event="post.event"
                  :call-to-action="post.callToAction"
                  :post-images="post.postImages"
                  :scheduled-at="post.scheduledAt ? post.scheduledAt : null"
                  :category-name="post.category ? post.category.name : null"
                  :repeat-configuration="post.repeatConfiguration ? toCamelCase(post.repeatConfiguration.kind) : null"
                  :editable="post.editable"
                  @delete-approval-post="clickDeleteApprovalPost($event)"
                  @open-preview-action-modal="openPreviewActionModal($event)"
                  @open-post-stores-modal="openPostStoresModal($event)"
                />
              </template>
            </tbody>
          </table>
        </div>
        <div v-if="posts.length > 0" class="p-controller">
          <div class="p-controller__count">
            <p class="p-controller__count__name">{{ $t('labels.perPageCount') }}</p>
            <div class="p-controller__count__select">
              <select :value="displayNumber" @change="changeDisplayNumber($event)">
                <option v-for="item in 6" :key="item" :value="displayNumbers[item - 1]">{{ displayNumbers[item - 1] }}</option>
              </select>
            </div>
            <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
          </div>
          <div class="p-controller__navi">
            <button
              type="button"
              :disabled="currentPage === 1"
              class="p-controller__navi__prev"
              @click="goPrevPage"
            >
              <Icon name="arrowLeft" />
            </button>
            <button
              type="button"
              :disabled="currentPage >= postsMeta.total_pages"
              class="p-controller__navi__next"
              @click="goNextPage"
            >
              <Icon name="arrowRight" />
            </button>
          </div>
        </div>
      </template>
    </CompaniesStorePostsLayout>
    <PostStoresModal
      :is-visible="isShowPostStoresModal"
      :google-map-stores="post.stores"
      :docomo-stores="post.docomoStores"
      :yahoo-map-stores="post.yahooMapStores"
      :loading="postLoading"
      :display-result="isDisplayResultOnPostStoreModal"
      :is-post-to-docomo-brand-page="post.docomo?.postToBrandPage"
      @close="isShowPostStoresModal = false"
    />
    <KkPromotionCompaniesStorePostsActionModal
      :is-preview="displayOnlyPreview"
      :show-action-modal="isShowPreviewActionModal"
      @post-approval-submit="postApprovalSubmit($event)"
      @close-action-modal="isShowPreviewActionModal = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { i18n } from '../plugins/i18n'
import CompaniesStorePostsLayout from '../layouts/CompaniesStorePostsLayout'
import Card from '../components/CompaniesStorePosts/Card'
import Loading from '../components/Loading'
import Icon from '../components/Icon.vue'
import { KkFilterModal } from '../components/projects'
import CompaniesStorePostsApprovalTableRow from '../components/CompaniesStorePosts/ApprovalTableRow'
import { KkPromotionCompaniesStorePostsActionModal, } from '../components/domains/promotion/CompaniesStorePosts'
import PostStoresModal from '../components/CompaniesStorePosts/Index/PostStoresModal'
import { addAttributes } from '../modules/filterModalConst'
import { camelCase } from '../modules/string_converter'
import { getQuery } from '../modules/getQuery'

export default {
  name: 'CompaniesStorePostsIndex',
  components: {
    CompaniesStorePostsApprovalTableRow,
    KkPromotionCompaniesStorePostsActionModal,
    KkFilterModal,
    CompaniesStorePostsLayout,
    Card,
    Loading,
    Icon,
    PostStoresModal,
  },
  beforeRouteLeave(to, from, next) {
    if (this.displayNumber === 20) this.setInitialDisplayNumber(50)
    next()
  },
  data() {
    return {
      displayOnlyPreview: false,
      loading: false,
      noLinked: false,
      currentPage: Number(this.$route.query['page']) || 1,
      isShowFilterModal: false,
      isShowPostStoresModal: false,
      isShowPreviewActionModal: false,
      filterItems: [],
    }
  },
  computed: {
    ...mapState({
      post: state => state.companiesStorePosts.post,
      postLoading: state => state.companiesStorePosts.postLoading,
      posts: state => state.companiesStorePosts.posts,
      postCounts: state => state.companiesStorePosts.postCounts,
      selectedApprovalStatusType: state => state.companiesStorePosts.selectedApprovalStatusType,
      approvalRequestNotes: state => state.companiesStorePosts.approvalRequestNotes,
      displayNumber: state => state.filters.displayNumber,
      meta: state => state.companiesStorePosts.meta,
      approvalInitMeta: state => state.companiesStorePosts.approvalInitMeta,
      formValues: state => state.companiesStorePosts.formValues,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      filteredCount: state => state.companiesStorePosts.meta.total_count,
      companyApprovalFlow: state => state.companiesPostsManagementsApprovalFlows.data,
    }),
    ...mapGetters({
      'canCreatePostAuthority': 'companiesAccessibleApi/canCreatePostAuthority',
      'canDeletePostAuthority': 'companiesAccessibleApi/canDeletePostAuthority',
      'selectedTopicType': 'companiesStorePosts/selectedTopicType',
      postStatusTypes: 'companiesStorePosts/postStatusTypes',
      selectedPostStatusType: 'companiesStorePosts/selectedPostStatusType',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
    }),
    currentCompanyUuid() { return this.$route.params.company_uuid },
    displayNumbers() { return [20, 50, 100, 150, 200, 250] },
    emptyDescription() {
      return this.isDocomoPlan
        ? this.$t('pages.CompaniesStorePost.emptyDescriptionExceptGoogle')
        : this.$t('pages.CompaniesStorePost.emptyDescription')
    },
    readPostsParams() {
      const query = {
        topic_types: this.formValues.topic_types,
        store_uuids: this.formValues.stores,
        brand_ids: this.formValues.brands,
        franchisee_ids: this.formValues.franchisees,
        area_ids: this.formValues.areas,
        prefectures: this.formValues.prefectures,
        user_company_ids: this.formValues.user_companies,
        category_ids: this.formValues.sns_categories,
        services: this.formValues.post_services,
        original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
        original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
        original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
        original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
        original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
      }

      if(this.selectedPostStatusType === 'POST'){
        query['statuses'] = this.formValues.statuses
      }

      if(this.selectedPostStatusType === 'IN_PROGRESS'){
        query['status'] = this.selectedApprovalStatusType
      }

      const payload = {
        search: query,
        per: this.displayNumber,
        page: this.currentPage,
      }

      if (this.isPublishedParams) {
        payload.search.is_published = this.isPublishedParams.is_published
      }

      return payload
    },
    menuList() {
      const menuList = {}

      for(const key in this.postStatusTypes) {
        if(key === 'IN_PROGRESS') {
          if(!this.companyApprovalFlow || !this.companyApprovalFlow.postApprovalFlow) continue
        }
        const countKey = camelCase(key.toLowerCase())
        if (this.postCounts[countKey] !== undefined && this.postCounts[countKey] !== null) {
          menuList[key] = `${this.postStatusTypes[key]}（${this.postCounts[countKey]}）`
        } else {
          menuList[key] = this.postStatusTypes[key]
        }
      }
      return menuList
    },
    postsMeta: state => {
      return state.meta
    },
    startDataNumber: function () {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber: function () {
      return this.startDataNumber + this.posts.length - 1
    },
    isPublishedParams() {
      if (this.selectedPostStatusType === 'POST') return { is_published: true }
      if (this.selectedPostStatusType === 'RESERVING') return { is_published: false }

      return null
    },
    isDisplayResultOnPostStoreModal() {
      return this.selectedPostStatusType === 'POST'
    },
  },
  async created() {
    this.resetPosts()

    const currentTab = this.$route.query.tab
    // 店舗が多いと投稿一覧が重くなるので初期表示を20件にして対応
    if(!getQuery('per')) this.setInitialDisplayNumber(20)

    if(currentTab === 'reserving') {
      this.updateSelectedPostStatusType('RESERVING')
    }

    if(currentTab === 'in_progress') {
      this.updateSelectedPostStatusType('IN_PROGRESS')
    }

    if(currentTab === 'draft') {
      this.updateSelectedPostStatusType('DRAFT')
    }

    this.loading = true
    await Promise.all([
      this.readApprovalFlow(),
      this.readPostCounts(),
      this.handleReadPosts(),
      this.readSnsCategories(),
      this.readSearchAttributes()
    ])

    this.setFormValues(this.formInitialValues())
    this.createFilter()

    this.loading = false
  },
  methods: {
    ...mapMutations('companiesStorePosts', [
      'resetPosts',
    ]),
    ...mapMutations({
      setFormValues: 'companiesStorePosts/setFormValues',
      updateSelectedPostStatusType: 'companiesStorePosts/updateSelectedPostStatusType',
      updateSelectedApprovalStatusType: 'companiesStorePosts/updateSelectedApprovalStatusType',
      setApprovalInitMeta: 'companiesStorePosts/setApprovalInitMeta'
    }),
    ...mapActions({
      readPosts: 'companiesStorePosts/readPosts',
      readPostCounts: 'companiesStorePosts/readPostCounts',
      readRequestingPosts: 'companiesStorePosts/readRequestingPosts',
      getPost: 'companiesStorePosts/getPost',
      readDraftedPost: 'companiesStorePosts/readDraftedPost',
      getRequestingPost: 'companiesStorePosts/getRequestingPost',
      updateApprovalRequest: 'companiesStorePosts/updateApprovalRequest',
      deletePost: 'companiesStorePosts/deletePost',
      deleteDraftedPost: 'companiesStorePosts/deleteDraftedPost',
      deleteRequestingPost: 'companiesStorePosts/deleteRequestingPost',
      deleteRequestApprovingPost: 'companiesStorePosts/deleteRequestApprovingPost',
      setDisplayNumber: 'filters/setDisplayNumber',
      setInitialDisplayNumber: 'filters/setInitialDisplayNumber',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      readPostsForFilter: 'companiesStorePosts/readPostsForFilter',
      readRequestingPostsForFilter: 'companiesStorePosts/readRequestingPostsForFilter',
      readDraftedPosts: 'companiesStorePosts/readDraftedPosts',
      readApprovalFlow: 'companiesPostsManagementsApprovalFlows/readApprovalFlow',
      readSnsCategories: 'companiesSnsCategories/readSnsCategories',
      updatePostCategory: 'companiesPostsCategories/updateCategories'
    }),
    async handleReadPosts() {
      switch (this.selectedPostStatusType) {
      case 'POST':
        await this.readPosts(this.readPostsParams)
        break
      case 'RESERVING':
        await this.readPosts(this.readPostsParams)
        break
      case 'IN_PROGRESS':
        await this.readRequestingPosts(this.readPostsParams)
        await this.setApprovalInitMeta(this.postsMeta)
        break
      case 'DRAFT':
        await this.readDraftedPosts({ per: this.displayNumber, page: this.currentPage })
        break
      }
    },
    async handleUpdatePostCategory({ postId, payload }) {
      this.loading = true
      const isSuccess = await this.updatePostCategory({ postId, payload })
      if(isSuccess) await this.readPosts(this.readPostsParams)
      this.loading = false
    },
    async clickApprovalSummary(key) {
      this.loading = true
      this.resetPage()
      // metaデータを取得するために絞り込みのない状態でリクエスト
      this.updateSelectedApprovalStatusType('all')
      await this.readRequestingPosts(this.readPostsParams)
      await this.setApprovalInitMeta(this.postsMeta)
      await this.readPostCounts()

      if(key === 'all') {
        this.loading = false
        return
      }

      // 絞り込みを実施
      this.updateSelectedApprovalStatusType(key)
      await this.readRequestingPosts(this.readPostsParams)
      this.loading = false
    },
    async clickMenu(key) {
      this.loading = true
      this.resetPage()
      this.updateSelectedPostStatusType(key)
      this.createFilter()
      switch (key) {
      case 'POST':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'RESERVING':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'IN_PROGRESS':
        await Promise.all([
          this.readPostCounts(),
          this.readRequestingPosts(this.readPostsParams),
        ])
        this.setApprovalInitMeta(this.postsMeta)
        break
      case 'DRAFT':
        await Promise.all([
          this.readPostCounts(),
          this.readDraftedPosts({ per: this.displayNumber, page: this.currentPage })
        ])
        break
      }
      this.loading = false
    },
    async clickDeletePost(id) {
      if (confirm(i18n.t('pages.CompaniesStorePost.confirmToDelete'))) {
        await this.deletePost({
          companyUuid: this.$route.params.company_uuid,
          id: id
        })
        switch (this.selectedPostStatusType) {
        case 'POST':
          await Promise.all([
            this.readPostCounts(),
            this.readPosts(this.readPostsParams)
          ])
          break
        case 'RESERVING':
          await Promise.all([
            this.readPostCounts(),
            this.readPosts(this.readPostsParams)
          ])
          break
        }
      }
    },
    async postApprovalSubmit(isApprove) {
      await this.updateApprovalRequest({
        id: this.post.id,
        isApprove: isApprove,
        approvalRequestNotes: this.approvalRequestNotes
      })
      this.isShowPreviewActionModal = false

      switch (this.selectedPostStatusType) {
      case 'POST':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'RESERVING':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'IN_PROGRESS':
        await Promise.all([
          this.readPostCounts(),
          this.readRequestingPosts(this.readPostsParams),
        ])
        this.setApprovalInitMeta(this.postsMeta)
        break
      }
    },
    async clickDeleteApprovalPost(param) {
      if (confirm(i18n.t('pages.CompaniesStorePost.confirmToDelete'))) {
        let res
        if(param.status === 'draft'){
          res = await this.deleteDraftedPost(param.id)
        }

        if (['rejected', 'requesting'].includes(param.status)) {
          res = await this.deleteRequestingPost(param.id)
        }

        if(res) {
          switch (this.selectedPostStatusType) {
          case 'IN_PROGRESS':
            await Promise.all([
              this.readPostCounts(),
              this.readRequestingPosts(this.readPostsParams),
            ])
            this.setApprovalInitMeta(this.postsMeta)
            break
          case 'DRAFT':
            await Promise.all([
              this.readPostCounts(),
              this.readDraftedPosts({ per: this.displayNumber, page: this.currentPage })
            ])
            break
          }
        }
      }
    },
    async clickDeleteRequestApprovalPost(param) {
      if (confirm(this.$t('pages.CompaniesStorePost.confirmToDeleteRequest'))) {
        if(param.status !== 'requesting'){ return }

        let res
        res = await this.deleteRequestApprovingPost(param.id)

        if(res) {
          await Promise.all([
            this.readPostCounts(),
            this.readRequestingPosts(this.readPostsParams),
          ])
          this.setApprovalInitMeta(this.postsMeta)
        }
      }
    },
    openPostStoresModal(postId) {
      switch (this.selectedPostStatusType) {
      case 'POST':
      case 'RESERVING':
        this.getPost({ companyUuid: this.currentCompanyUuid, id: postId })
        break
      case 'IN_PROGRESS':
        this.getRequestingPost({ id: postId })
        break
      case 'DRAFT':
        this.readDraftedPost({ id: postId })
        break
      }
      this.isShowPostStoresModal = true
    },
    openPreviewActionModal({ postId, onlyPreview = true }) {
      this.displayOnlyPreview = onlyPreview
      switch (this.selectedPostStatusType) {
      case 'POST':
      case 'RESERVING':
        this.getPost({ companyUuid: this.currentCompanyUuid, id: postId })
        break
      case 'IN_PROGRESS':
        this.getRequestingPost({ id: postId })
        break
      case 'DRAFT':
        this.readDraftedPost({ id: postId })
        break
      }
      this.isShowPreviewActionModal = true
    },
    goPrevPage() {
      --this.currentPage
      this.changePage()
    },
    goNextPage() {
      ++this.currentPage
      this.changePage()
    },
    async changePage() {
      this.loading = true
      window.scrollTo({ top: 0 })
      switch (this.selectedPostStatusType) {
      case 'POST':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'RESERVING':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'IN_PROGRESS':
        await Promise.all([
          this.readPostCounts(),
          this.readRequestingPosts(this.readPostsParams)
        ])
        break
      case 'DRAFT':
        await Promise.all([
          this.readPostCounts(),
          this.readDraftedPosts({ per: this.displayNumber, page: this.currentPage })
        ])
        break
      }
      this.loading = false
    },
    /**
     * 表示件数の変更
     * @param  { Object } e イベントオブジェクト
     * @return { Void }
     */
    async changeDisplayNumber(e) {
      this.loading = true
      this.resetPage()
      this.setDisplayNumber(e.target.value)
      const selectedApprovalStatusType = this.selectedApprovalStatusType

      switch (this.selectedPostStatusType) {
      case 'POST':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'RESERVING':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'IN_PROGRESS':
        // metaデータを取得するために絞り込みのない状態でリクエスト
        this.updateSelectedApprovalStatusType('all')
        await Promise.all([
          this.readPostCounts(),
          this.readRequestingPosts(this.readPostsParams)
        ])

        await this.setApprovalInitMeta(this.postsMeta)

        this.updateSelectedApprovalStatusType(selectedApprovalStatusType)
        await this.readRequestingPosts(this.readPostsParams)
        break
      case 'DRAFT':
        await Promise.all([
          this.readPostCounts(),
          this.readDraftedPosts({ per: this.displayNumber, page: this.currentPage })
        ])
        break
      }

      this.loading = false
    },
    resetPage() {
      this.currentPage = 1
      this.$route.query.page = undefined
    },
    formInitialValues() {
      return {
        statuses: [],
        topic_types: [],
        stores: [],
        brands: [],
        franchisees: [],
        user_companies: [],
        sns_categories: [],
        post_services: [],
        areas: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)

      switch (this.selectedPostStatusType) {
      case 'POST':
        this.readPostsForFilter(this.readPostsParams)
        break
      case 'RESERVING':
        this.readPostsForFilter(this.readPostsParams)
        break
      case 'IN_PROGRESS':
        this.readRequestingPostsForFilter(this.readPostsParams)
        break
      }
    },
    async resetFilter() {
      this.isShowFilterModal = false
      this.loading = true
      this.setFormValues(this.formInitialValues())

      switch (this.selectedPostStatusType) {
      case 'POST':
        await Promise.all([
          this.readPostCounts(),
          this.readPostsForFilter(this.readPostsParams),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'RESERVING':
        await Promise.all([
          this.readPostCounts(),
          this.readPostsForFilter(this.readPostsParams),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'IN_PROGRESS':
        await Promise.all([
          this.readPostCounts(),
          this.readRequestingPostsForFilter(this.readPostsParams),
          this.readRequestingPosts(this.readPostsParams)
        ])
        await this.setApprovalInitMeta(this.postsMeta)
        break
      }
      this.loading = false
    },
    async handleResetPageAndSubmit() {
      this.loading = true
      this.resetPage()
      this.isShowFilterModal = false
      switch (this.selectedPostStatusType) {
      case 'POST':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'RESERVING':
        await Promise.all([
          this.readPostCounts(),
          this.readPosts(this.readPostsParams)
        ])
        break
      case 'IN_PROGRESS':
        await Promise.all([
          this.readPostCounts(),
          this.readRequestingPosts(this.readPostsParams),
        ])
        this.setApprovalInitMeta(this.postsMeta)
        break
      }
      this.loading = false
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.topic_types,
        this.searchAttributes.sns_categories,
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ]

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)

      if (this.selectedPostStatusType === 'POST') {
        if (this.searchAttributes.statuses) attributes.unshift(this.searchAttributes.statuses)
      }

      attributes.unshift(this.searchAttributes.post_services)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    },
    toCamelCase(str) {
      return camelCase(str)
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../../../stylesheets/biz_spa/foundation/_mixin.scss';

.info {
  margin-bottom: 16px;
}

.requestingPostHeader {
  display: flex;
  @media screen and (max-width: 767px) {
    display: block;
  }

  .p-filtersA {
    width: unset;
  }
}
.approvalSummary {
  display: flex;
  flex-direction: row;
  @media screen and (max-width: 767px) {
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .item {
    background-color: #ECEDF0;
    border-radius: 4px;
    width: 200px;
    height: 65px;
    margin: 8px;
    display: flex;
    flex-direction: column;
    font-size: 10px;
    font-weight: bold;
    @media screen and (max-width: 767px) {
      flex-basis: calc((100% - 16px) / 3);
      width: auto;
      margin: 0;
    }

    &.isActive {
      background-color: #F8F9FA;
    }

    .title {
      text-align: center;
      margin: 8px 0;
    }

    .summary {
      text-align: center;
      margin-bottom: 8px;

      span {
        cursor: pointer;
        font-weight: bold;
        font-size: 18px;
        line-height: 24px;
        text-align: center;
        text-decoration-line: underline;
      }
    }
  }
}
.approvalTable {
  border-spacing: 0 8px;
  @media screen and (max-width: 767px) {
    display: block;
    thead {
      display: none;
    }
    tbody {
      display: block;
    }
  }
}
.c-button {
  @media screen and (max-width: 768px) {
    margin-left: auto;
    margin-bottom: 20px;
  }
}

</style>
